"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { getAiCreditPaymentReceipt } from "@/lib/actions/ai-credit-payment-receipt";
import { formatCredits } from "@/lib/ai-credits/monthly-policy";
const MAX_CHECKS = 10;
const RETRY_DELAY_MS = 1000;
const announced = new Set<string>();
function firstAnnouncement(id: string) {
  const key = `ai-credit-receipt:${id}`;
  if (announced.has(key)) return false;
  announced.add(key);
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "shown");
  } catch {
    // Storage may be disabled; the in-memory set still prevents duplicate toasts.
  }
  return true;
}

/** Keep the return reference after URL cleanup and allow inline checkout to start a check. */
export function useAiCreditPaymentNotice(initialReference: string | null) {
  const [reference, setReference] = useState(initialReference);
  const {
    refresh
  } = useRouter();
  useEffect(() => {
    if (!reference) return;
    let canceled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    async function check() {
      attempts++;
      let receipt = null;
      try {
        receipt = await getAiCreditPaymentReceipt(reference!);
      } catch {/* Retry transient confirmation errors without reporting a failed charge. */}
      if (canceled) return;
      if (receipt) {
        if (firstAnnouncement(receipt.id)) {
          refresh();
          toast.success(`${formatCredits(receipt.credits)} credits added`, {
            id: receipt.id,
            duration: 6000
          });
        }
        setReference(null);
        return;
      }
      if (attempts < MAX_CHECKS) {
        timer = setTimeout(check, RETRY_DELAY_MS);
      } else {
        toast.message("We couldn’t confirm your credits yet. Refresh to check your balance.");
        setReference(null);
      }
    }
    void check();
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [reference, refresh]);
  return setReference;
}